/**
 * TanStack Query hooks for the Stickyboard surface. Mirrors the VAU hooks
 * pattern: queries pair a fetcher with its `.key()`; mutations invalidate the
 * affected query keys and surface a toast on failure.
 */

import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInstanceId } from '@frontend/hooks';

import {
  createSection,
  type CreateSectionPayload,
  createStack,
  type CreateStackPayload,
  curateBoard,
  deleteSection,
  deleteStack,
  dismissSticky,
  listSections,
  listStacks,
  listStickies,
  patchSticky,
  type SectionPatch,
  type StackPatch,
  type StickyFilter,
  type StickyListResponse,
  type StickyUpdatePayload,
  updateSection,
  updateStack,
} from '../services/stickyboard';

const STICKIES_KEY = ['stickyboard', 'stickies'] as const;
// The board's exact list key (status=active). Built inline (not via
// `listStickies.key`, whose `as const` tuple breaks TanStack's updater typing).
const ACTIVE_STICKIES_KEY = ['stickyboard', 'stickies', { status: 'active' }];

// ---------- queries ----------

export function useStickies(filter: StickyFilter = {}) {
  return useQuery({
    queryKey: listStickies.key(filter),
    queryFn: listStickies(filter),
  });
}

export function useSections() {
  return useQuery({
    queryKey: listSections.key(),
    queryFn: listSections(),
  });
}

export function useStacks() {
  return useQuery({
    queryKey: listStacks.key(),
    queryFn: listStacks(),
  });
}

// ---------- sticky mutations ----------

export function usePatchSticky() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stickyId, payload }: { stickyId: string; payload: StickyUpdatePayload }) =>
      patchSticky(stickyId, payload),
    // Optimistic: a drag-to-move (or pin) applies the override fields to the
    // board's active-sticky list immediately, so the card jumps lanes/stacks
    // without waiting for the round-trip or the socket's debounced refetch.
    // Snapshot for rollback on error; reconcile via invalidate on settle.
    onMutate: async ({ stickyId, payload }) => {
      await queryClient.cancelQueries({ queryKey: STICKIES_KEY });
      const previous = queryClient.getQueryData<StickyListResponse>(ACTIVE_STICKIES_KEY);
      queryClient.setQueryData<StickyListResponse>(ACTIVE_STICKIES_KEY, old => {
        if (!old) return old;
        return {
          ...old,
          stickies: old.stickies.map(s => (s.sticky_id === stickyId ? { ...s, ...payload } : s)),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(ACTIVE_STICKIES_KEY, context?.previous);
      toaster.create('Could not update sticky', { type: 'danger' });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: STICKIES_KEY }),
  });
}

/**
 * Persists the user's own interactive-card state (e.g. checklist toggles).
 * Deliberately does NOT invalidate the board: the card holds the live state
 * locally for the session, so a refetch would only churn the layout (and risk
 * clobbering an in-flight toggle). The saved value hydrates the card on the
 * next full board load instead.
 */
export function useSaveStickyCardData() {
  return useMutation({
    mutationFn: ({ stickyId, data }: { stickyId: string; data: Record<string, unknown> }) =>
      patchSticky(stickyId, { user_card_data: data }),
    onError: () => toaster.create('Could not save your changes to this card', { type: 'danger' }),
  });
}

export function useDismissSticky() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stickyId: string) => dismissSticky(stickyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STICKIES_KEY }),
    onError: () => toaster.create('Could not dismiss sticky', { type: 'danger' }),
  });
}

// ---------- section mutations ----------

export function useCreateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSectionPayload) => createSection(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listSections.key() });
      toaster.create('Section created', { type: 'success' });
    },
    onError: () => toaster.create('Could not create section', { type: 'danger' }),
  });
}

export function useUpdateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, patch }: { sectionId: string; patch: SectionPatch }) =>
      updateSection(sectionId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listSections.key() }),
    onError: () => toaster.create('Could not update section', { type: 'danger' }),
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => deleteSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listSections.key() });
      queryClient.invalidateQueries({ queryKey: STICKIES_KEY });
    },
    onError: () => toaster.create('Could not delete section', { type: 'danger' }),
  });
}

// ---------- stack mutations ----------

export function useCreateStack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStackPayload) => createStack(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listStacks.key() });
      toaster.create('Stack created', { type: 'success' });
    },
    onError: () => toaster.create('Could not create stack', { type: 'danger' }),
  });
}

export function useUpdateStack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stackId, patch }: { stackId: string; patch: StackPatch }) =>
      updateStack(stackId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listStacks.key() }),
    onError: () => toaster.create('Could not update stack', { type: 'danger' }),
  });
}

export function useDeleteStack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stackId: string) => deleteStack(stackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listStacks.key() });
      queryClient.invalidateQueries({ queryKey: STICKIES_KEY });
    },
    onError: () => toaster.create('Could not delete stack', { type: 'danger' }),
  });
}

// ---------- curate (fire-and-forget on board open) ----------

export function useCurateBoard() {
  const { instanceId } = useInstanceId();
  return useMutation({
    mutationFn: () => {
      if (!instanceId) throw new Error('instanceId required');
      return curateBoard(instanceId);
    },
    // Best-effort: the Steward may change nothing. Results stream back over the
    // realtime socket; we don't toast on success to keep the open calm.
    onError: () => {
      /* silent — curation is advisory */
    },
  });
}
